import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/features/auth/api/auth.api';
import { useAuth } from '@/contexts/AuthContext';
import { notify } from '@/lib/notify';
import { isApiError } from '@/api/errors';
import type { TenantMembership } from '@/types/auth';

interface TenantContextValue {
  current: { id: string; name: string } | null;
  memberships: TenantMembership[];
  isMulti: boolean;
  isSwitching: boolean;
  isLoading: boolean;
  switchTo: (tenantId: string) => Promise<void>;
}

const TenantContext = createContext<TenantContextValue | null>(null);

/**
 * Tenant switching. Reads the user's tenant memberships and re-scopes the
 * session to another tenant via AuthContext (which clears cached data). Only
 * fetches memberships when authenticated.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const { status, user, switchTenant } = useAuth();
  const navigate = useNavigate();
  const [isSwitching, setIsSwitching] = useState(false);

  const { data: memberships = [], isLoading: membershipsLoading } = useQuery({
    queryKey: ['auth', 'tenants'],
    queryFn: authApi.tenants,
    enabled: status === 'authenticated',
    staleTime: 300_000,
    retry: false,
  });

  const activeMembership = memberships.find((m) => m.id === user?.tenantId);
  const current = user
    ? { id: user.tenantId, name: activeMembership?.name ?? user.tenantName ?? 'Organization' }
    : null;

  const switchTo = useCallback(async (tenantId: string) => {
    if (tenantId === user?.tenantId) return;
    setIsSwitching(true);
    try {
      await switchTenant(tenantId);
      const name = memberships.find((m) => m.id === tenantId)?.name ?? 'organization';
      notify.success(`Switched to ${name}`);
      navigate('/');
    } catch (e) {
      notify.error(isApiError(e) && e.status === 403
        ? 'You do not have access to that organization.'
        : 'Could not switch organization. Please try again.');
    } finally {
      setIsSwitching(false);
    }
  }, [user?.tenantId, switchTenant, memberships, navigate]);

  const value = useMemo(
    () => ({ current, memberships, isMulti: memberships.length > 1, isSwitching, isLoading: membershipsLoading, switchTo }),
    [current, memberships, isSwitching, membershipsLoading, switchTo],
  );
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
}
